/* eslint-disable @typescript-eslint/no-explicit-any */
import { eventUtil, MapComponent, MapInfoWindow, MapMarker } from '@marriott/mi-ui-library';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { searchResultsState, useStore } from '../../../modules/store/searchResultsStore';
import { useEffect, useRef, useState } from 'react';
import { getTitleIconOutlet, useGetBreakpoint } from '../../../modules/utils/helpers';
import { SearchOutletMiniCard } from '../SearchOutletMiniCard';
import { miniCardHeight } from '../../../modules/utils/constants/constants';
import { GlobalStyle, StyledGoogleMapContainer } from './GoogleMapComponent.styles';
import { SearchMiniCardSlider } from '../SearchMiniCardSlider/index';
import clsx from 'clsx';
import { StyledErrorMessage } from '../../../molecules/RnBErrorMessage/RnBErrorMessage.styles';
import { rnbErrorState, rnbErrorStore } from '../../../modules/store/rnbErrorStore';

export const GoogleMapComponent = (props: any) => {
  const [latitude] = useState<number>(useStore((state: searchResultsState) => state.lat));
  const [longitude] = useState<number>(useStore((state: searchResultsState) => state.long));
  const searchResultData = useStore((state: searchResultsState) => state.searchResultData);
  const [updatedSearchData] = useState<any>(searchResultData?.edges);
  const [selectedOutlet, setSelectedOutlet] = useState<any>([]);
  const [showMiniCard, setShowMiniCard] = useState(false);
  const [mapCenter, setMapCenter] = useState<any>({});
  const [activeMarker, setActiveMarker] = useState<any>();
  const [isMobileViewPort, setIsMobileViewPort] = useState(useGetBreakpoint() === 'mobile');
  const mapRef = useRef<google.maps.Map | null>(null);
  const divRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const errorMessage = rnbErrorStore((state: rnbErrorState) => state.errorMessage);
  const didMountRef = useRef(false);

  const GOOGLE_MAP_API_KEY: any = useClientEnvVarsStore.getState().envVarsObject['GOOGLE_MAP_API_KEY'];

  const mapOptions = {
    fullscreenControl: false,
    mapTypeControl: false,
    zoomControl: true,
    gestureHandling: 'greedy',
    mapId: 'ec5bb275520a7fc8',
    mapTypeId: 'roadmap',
  };

  useEffect(() => {
    const handleResize = (entries: any) => {
      for (const entry of entries) {
        if (entry.contentRect) {
          setContainerWidth(entry.contentRect.width);
        }
      }
    };

    const observer = new window.ResizeObserver(handleResize);
    if (divRef.current) {
      observer.observe(divRef.current);
    }
    return () => observer.disconnect();
  }, []);

  const mapStyle = { height: '100%', width: '100%' };

  const getOutletIcon = (outlet: any, index: number) => {
    let iconClass = getTitleIconOutlet(outlet?.node) ? props?.diningIconPath : props?.barIconPath;
    if (activeMarker === index) iconClass += ' active-map-pin';
    return iconClass + ' m-map-pin flex-map-pin pin-0';
  };

  const getMarkerLabelText = (outlet: any) => {
    const similarOutlets = updatedSearchData?.filter(
      (outletList: any) =>
        outletList?.node?.property?.basicInformation?.latitude === outlet?.node?.property?.basicInformation?.latitude &&
        outletList?.node?.property?.basicInformation?.latitude === outlet?.node?.property?.basicInformation?.latitude &&
        outletList?.node?.id !== outlet?.node?.id
    );
    if (similarOutlets?.length > 0) return ` (${similarOutlets?.length + 1})`;
    else return ' ';
  };

  const getInfoWindowDetails = (outlet: any) => {
    return <SearchOutletMiniCard outlet={outlet} {...props} />;
  };

  const handleOutletSelection = (index: number) => {
    const selectedOutletInfo = updatedSearchData[index];
    const similarOutlet = updatedSearchData?.filter(
      (outletList: any) =>
        outletList?.node?.property?.basicInformation?.latitude ===
          selectedOutletInfo?.node?.property?.basicInformation?.latitude &&
        outletList?.node?.property?.basicInformation?.latitude ===
          selectedOutletInfo?.node?.property?.basicInformation?.latitude &&
        outletList?.node?.id !== selectedOutletInfo?.node?.id
    );
    if (similarOutlet?.length > 0) setSelectedOutlet([...similarOutlet, selectedOutletInfo]);
    else setSelectedOutlet([selectedOutletInfo]);
    setMapCenter({
      lat: selectedOutletInfo?.node?.property?.basicInformation?.latitude,
      lng: selectedOutletInfo?.node?.property?.basicInformation?.longitude,
    });
    setShowMiniCard(true);
  };

  const getInfoWindowOffset = (index: number) => {
    if (selectedOutlet?.length === 1) return new window.google.maps.Size(0, -30);
    else {
      return new window.google.maps.Size(0, -(index * miniCardHeight + index * 10 + 20));
    }
  };

  const mapContent = () => {
    return updatedSearchData?.map((outlet: any, index: number) => {
      return (
        <>
          <MapMarker
            label={{
              className: getOutletIcon(outlet, index),
              color: activeMarker === index ? 'black' : 'white',
              fontSize: '16px',
              text: getMarkerLabelText(outlet),
            }}
            icon={{
              url: '',
              scaledSize: new (window as any).google.maps.Size(75, 20),
            }}
            position={{
              lat: outlet?.node?.property?.basicInformation?.latitude,
              lng: outlet?.node?.property?.basicInformation?.longitude,
            }}
            onClick={() => {
              setActiveMarker(index);
              handleOutletSelection(index);
              setTimeout(() => {
                const mapMarkers = document.getElementsByClassName('gm-style-iw-tc');
                Array.from(mapMarkers)?.forEach((mapMarker: any, index: number) => {
                  if (index !== 0) mapMarker?.classList.add('d-none');
                });
              }, 200);
            }}
          />

          {showMiniCard &&
            selectedOutlet?.length > 0 &&
            activeMarker === index &&
            (isMobileViewPort ? (
              <SearchMiniCardSlider
                searchResults={updatedSearchData}
                {...props}
                activeMarker={activeMarker}
                setActiveMarker={setActiveMarker}
                handleOutletSelection={handleOutletSelection}
                isMobileView={true}
                containerWidth={containerWidth}
              />
            ) : selectedOutlet?.length > 2 ? (
              <MapInfoWindow
                position={{
                  lat: selectedOutlet[0]?.node?.property?.basicInformation?.latitude,
                  lng: selectedOutlet[0]?.node?.property?.basicInformation?.longitude,
                }}
                children={
                  <SearchMiniCardSlider
                    searchResults={selectedOutlet}
                    {...props}
                    activeMarker={activeMarker}
                    setActiveMarker={setActiveMarker}
                    handleOutletSelection={handleOutletSelection}
                    isMobileView={false}
                    containerWidth={containerWidth}
                  />
                }
                options={{ pixelOffset: new window.google.maps.Size(0, -30) }}
              />
            ) : (
              selectedOutlet?.map((outlet: any, index: number) => (
                <MapInfoWindow
                  position={{
                    lat: outlet?.node?.property?.basicInformation?.latitude,
                    lng: outlet?.node?.property?.basicInformation?.longitude,
                  }}
                  children={getInfoWindowDetails(outlet)}
                  options={{ pixelOffset: getInfoWindowOffset(index) }}
                />
              ))
            ))}
        </>
      );
    });
  };

  const closeInfoWindow = () => {
    if (showMiniCard && selectedOutlet) {
      setActiveMarker(null);
      setSelectedOutlet([]);
      setShowMiniCard(false);
    }
  };

  const onMapLoad = (map: google.maps.Map) => {
    mapRef.current = map;
  };

  const handleCenterChange = () => {
    if (mapRef.current) {
      const center = mapRef.current.getCenter();
      const lat = center?.lat();
      const lng = center?.lng();

      // This is to prevent Label change on initial Map Load
      if (mapCenter?.lat !== lat && mapCenter?.lng !== lng)
        eventUtil.dispatch('updateCtaLabel', [{ lat: lat, lng: lng }]);
    }
  };

  const handleZoomChange = () => {
    if (!didMountRef.current) {
      didMountRef.current = true; // Skip the first call (initial load)
      return;
    }
    if (mapRef.current) {
      const center = mapRef.current.getCenter();
      const lat = center?.lat();
      const lng = center?.lng();
      eventUtil.dispatch('updateCtaLabel', [{ lat: lat, lng: lng }]);
    }
  };

  eventUtil.on('showInfoWindow', (index: number) => {
    setActiveMarker(index);
    setShowMiniCard(true);
    setSelectedOutlet([updatedSearchData[index]]);
    setMapCenter({
      lat: updatedSearchData[index]?.node?.property?.basicInformation?.latitude,
      lng: updatedSearchData[index]?.node?.property?.basicInformation?.longitude,
    });
  });

  eventUtil.on('hideInfoWindow', outlet => {
    setActiveMarker(null);
    setSelectedOutlet([outlet]);
    setShowMiniCard(false);
  });

  useEffect(() => {
    if (latitude !== 0 && longitude !== 0) {
      setMapCenter({ lat: latitude, lng: longitude });
    } else if (
      updatedSearchData &&
      updatedSearchData?.length > 0 &&
      !!updatedSearchData[0]?.node?.property?.basicInformation?.latitude &&
      !!updatedSearchData[0]?.node?.property?.basicInformation?.longitude
    ) {
      setMapCenter({
        lat: updatedSearchData[0]?.node?.property?.basicInformation?.latitude,
        lng: updatedSearchData[0]?.node?.property?.basicInformation?.longitude,
      });
    } else {
      setMapCenter({ lat: 0, lng: 0 });
    }
  }, []);

  useEffect(() => {
    function handleResize() {
      // eslint-disable-next-line react-hooks/rules-of-hooks
      setIsMobileViewPort(useGetBreakpoint() === 'mobile');
    }
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return updatedSearchData && updatedSearchData?.length > 0 ? (
    <StyledGoogleMapContainer data-testid="google-map-component" id="google-map-component" ref={divRef}>
      {!isMobileViewPort && selectedOutlet?.length > 2 && <GlobalStyle />}
      {errorMessage && (
        <StyledErrorMessage
          data-component-name="m-rnb-RnBErrorMessage"
          data-testid="rnb-RnBErrorMessage"
          isEmpty={!errorMessage}
          className={clsx(
            'container rnb-error-container p-0 mx-3 rnb-additional-container',
            errorMessage ? 'error-message-active' : ''
          )}
        >
          <div className={'m-message-inline error-sev2 rnb-error-message mt-3 mb-3'}>
            <div className="m-message-content-wrap">
              <div className="m-message-content">
                <span>
                  {props?.rnberror[errorMessage as keyof typeof props]
                    ? props?.rnberror[errorMessage as keyof typeof props]
                    : errorMessage}
                </span>
              </div>
            </div>
          </div>
        </StyledErrorMessage>
      )}
      <MapComponent
        googleMapsApiKey={GOOGLE_MAP_API_KEY}
        mapId="ec5bb275520a7fc8"
        mapHeight="100%"
        options={mapOptions}
        mapContainerStyle={mapStyle}
        zoom={10}
        center={mapCenter}
        children={mapContent()}
        onClick={closeInfoWindow}
        onDragStart={closeInfoWindow}
        onLoad={onMapLoad}
        onCenterChanged={handleCenterChange}
        onZoomChanged={handleZoomChange}
      />
    </StyledGoogleMapContainer>
  ) : (
    <div className={clsx('loadStyle', 'm-spinner-m')} data-testid="map-loader"></div>
  );
};
